import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Fingerprint, Brain, Lightbulb, Compass, Path } from '@phosphor-icons/react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICONS = {
  interaktion: Fingerprint,
  informationsfluss: Lightbulb,
  entscheidungslogik: Brain,
  strukturpraeferenz: Compass,
};

const CognitiveMatrixPhase = ({ sessionId, onComplete, onBack }) => {
  const [questions, setQuestions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});  // dimension → 0..100
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/cognitive/questions`).then(r => {
      setQuestions(r.data.questions || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const current = questions[currentIdx];
  const totalQ = questions.length;
  const progress = totalQ > 0 ? ((currentIdx + 1) / totalQ) * 100 : 0;

  // 5-point bipolar scale mapped to 0-100
  const scaleOptions = [
    { value: 0, label: 'Klar Pol A' },
    { value: 25, label: 'Eher Pol A' },
    { value: 50, label: 'Beides' },
    { value: 75, label: 'Eher Pol B' },
    { value: 100, label: 'Klar Pol B' },
  ];

  const pickScale = (dimension, value) => {
    setAnswers(prev => ({ ...prev, [dimension]: value }));
  };

  const goNext = async () => {
    if (currentIdx < totalQ - 1) {
      setCurrentIdx(currentIdx + 1);
      window.scrollTo(0, 0);
      return;
    }
    // Submit
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/user/cognitive-matrix`, {
        session_id: sessionId,
        scores: answers,
      });
      onComplete(res.data.cognitive_type);
    } catch (err) {
      console.error(err);
      setSubmitting(false);
    }
  };

  const goBack = () => {
    if (currentIdx > 0) setCurrentIdx(currentIdx - 1);
    else if (onBack) onBack();
  };

  if (loading) {
    return <div className="py-20 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>Kalibrierung lädt…</div>;
  }
  if (!current) return null;

  const Icon = ICONS[current.id] || Brain;
  const selectedValue = answers[current.dimension];

  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -15 }}
      transition={{ duration: 0.35 }} data-testid="cognitive-matrix-phase">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold" style={{ color: 'var(--text-secondary)', letterSpacing: '0.1em' }}>
            KOGNITIVE MATRIX · {currentIdx + 1} / {totalQ}
          </p>
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{Math.round(progress)}%</p>
        </div>
        <div className="h-1" style={{ background: 'var(--border-default)', borderRadius: 9999, overflow: 'hidden' }}>
          <motion.div animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }}
            className="h-full" style={{ background: 'var(--accent-cyan)', borderRadius: 9999 }} />
        </div>
      </div>

      {/* Question Card */}
      <AnimatePresence mode="wait">
        <motion.div key={current.id}
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="mb-6 p-6"
          style={{
            background: 'rgba(91,184,196,0.05)',
            border: '1px solid rgba(91,184,196,0.2)',
            borderRadius: '20px',
          }}>
          <div className="flex items-start gap-3 mb-5">
            <div className="flex items-center justify-center flex-shrink-0"
              style={{ width: 44, height: 44, borderRadius: 14, background: 'var(--accent-cyan)' }}>
              <Icon size={22} weight="duotone" color="#fff" />
            </div>
            <div>
              <p className="text-xs font-semibold" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.1em' }}>
                {current.title.toUpperCase()}
              </p>
              <p className="text-base font-semibold mt-1" style={{ color: 'var(--text-primary)', lineHeight: 1.4 }}>
                {current.prompt}
              </p>
            </div>
          </div>

          {/* Bipolar Tiles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
            <div className="p-4" style={{
              background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
              borderRadius: '14px',
            }}>
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.08em' }}>POL A</p>
              <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{current.pole_left.label}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {current.pole_left.description}
              </p>
            </div>
            <div className="p-4" style={{
              background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
              borderRadius: '14px',
            }}>
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--accent-cyan)', letterSpacing: '0.08em' }}>POL B</p>
              <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{current.pole_right.label}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {current.pole_right.description}
              </p>
            </div>
          </div>

          {/* 5-Point Scale */}
          <div className="flex items-center justify-between gap-2">
            {scaleOptions.map((opt) => {
              const active = selectedValue === opt.value;
              return (
                <button key={opt.value}
                  onClick={() => pickScale(current.dimension, opt.value)}
                  data-testid={`matrix-scale-${current.id}-${opt.value}`}
                  className="flex-1 py-3 flex flex-col items-center justify-center transition-all"
                  style={{
                    background: active ? 'var(--accent-cyan)' : 'var(--bg-surface)',
                    color: active ? '#fff' : 'var(--text-secondary)',
                    border: `1px solid ${active ? 'var(--accent-cyan)' : 'var(--border-default)'}`,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transform: active ? 'scale(1.03)' : 'scale(1)',
                    boxShadow: active ? '0 6px 16px rgba(91,184,196,0.35)' : 'none',
                  }}>
                  <div className="w-5 h-5 rounded-full mb-1" style={{
                    background: active ? '#fff' : `linear-gradient(90deg, var(--accent-cyan) ${100 - opt.value}%, var(--border-default) ${100 - opt.value}%)`,
                    border: active ? '2px solid var(--accent-cyan)' : 'none',
                  }} />
                  <span className="text-[10px] font-semibold" style={{ letterSpacing: '0.04em' }}>{opt.label}</span>
                </button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Actions */}
      <div className="flex items-center justify-between gap-3">
        <button onClick={goBack} disabled={submitting}
          className="flex items-center gap-2 px-4 py-2.5 text-xs font-semibold transition-all"
          style={{
            background: 'transparent', border: '1px solid var(--border-default)',
            borderRadius: '10px', color: 'var(--text-secondary)', cursor: 'pointer',
          }}
          data-testid="matrix-back-btn">
          <ArrowLeft size={14} /> Zurück
        </button>
        <button onClick={goNext} disabled={selectedValue === undefined || submitting}
          className="flex items-center gap-2 px-6 py-2.5 text-xs font-semibold transition-all"
          style={{
            background: selectedValue === undefined ? 'var(--border-default)' : 'var(--accent-cyan)',
            color: '#fff', border: 'none',
            borderRadius: '10px', cursor: selectedValue === undefined ? 'not-allowed' : 'pointer',
            boxShadow: selectedValue === undefined ? 'none' : '0 4px 14px rgba(91,184,196,0.3)',
            opacity: selectedValue === undefined ? 0.5 : 1,
          }}
          data-testid="matrix-next-btn">
          {submitting ? 'Wird kalibriert…' : currentIdx === totalQ - 1 ? 'Kalibrierung abschließen' : 'Weiter'}
          {!submitting && <ArrowRight size={14} weight="bold" />}
        </button>
      </div>
    </motion.div>
  );
};

export default CognitiveMatrixPhase;
